import { useRef, useMemo } from 'react'
import { Appointment, TaskListItem, TechnicianAppointmentsItem, TimeManagementListItem } from '@myapp/contract'
import {
  CAL_HOURS,
  CAL_TOTAL_HEIGHT,
  HOUR_HEIGHT,
  CAL_START_HOUR,
  CAL_END_HOUR,
  getTopPx,
  getHeightPx,
  getHourFromIso,
} from '../../worker-calendar/model/calendarConstants'
import { computeTransfer } from '../../worker-calendar/model/transferDetection'
import { computeOverlapLayout, getAppointmentRect, getTaskRect, getTMRect, getBreakRect } from '../../worker-calendar/model/overlapLayout'
import { resolveBreakForDay } from '../../../entities/time-entry/model/useBreakBlock'
import { isWorkingDay } from '../../../shared/lib/timeline/weekHelpers'
import { WorkerAppointmentBlock } from '../../worker-calendar/ui/WorkerAppointmentBlock'
import { WorkerTaskBlock } from '../../worker-calendar/ui/WorkerTaskBlock'
import { WorkerTMBlock } from '../../worker-calendar/ui/WorkerTMBlock'
import { WorkerBreakBlock } from '../../worker-calendar/ui/WorkerBreakBlock'
import type { DragState, DragType } from '../model/usePlanningDrag'

const DAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const MONTH_FORMATTER = new Intl.DateTimeFormat('de-DE', { month: 'short' })

const DEFAULT_DRAFT_WORK_COLOR = '#124093'
const DEFAULT_DRAFT_TRAVEL_COLOR = '#5B7EC9'

export interface DraftBlock {
  plannedStart: string // work start ISO
  workDuration: number
  travelDuration: number
  departureDuration: number
  title: string
}

interface Props {
  dayIso: string
  isToday: boolean
  technicianData: TechnicianAppointmentsItem | null
  existingTasks: TaskListItem[]
  currentUserId: number
  draft: DraftBlock | null
  onTimeSelect: (dayIso: string, hours: number, minutes: number) => void
  draftColors?: { work: string; travel: string }
  /** Current drag state from usePlanningDrag */
  drag?: DragState | null
  /** Called to start a drag on a block */
  onDragStart?: (type: DragType, dayIso: string, e: React.PointerEvent, gridEl: HTMLElement, blockTopPx: number) => void
}

function filterForDay(appointments: Appointment[], dayIso: string): Appointment[] {
  return appointments.filter((a) => new Date(a.plannedStart).toISOString().slice(0, 10) === dayIso)
}

function filterTMForDay(entries: TimeManagementListItem[], dayIso: string): TimeManagementListItem[] {
  const dayStart = new Date(`${dayIso}T00:00:00`)
  const dayEnd = new Date(`${dayIso}T23:59:59.999`)
  return entries.filter((e) => new Date(e.startTime) <= dayEnd && new Date(e.endTime) >= dayStart)
}

function filterTasksForDay(tasks: TaskListItem[], dayIso: string): TaskListItem[] {
  return tasks.filter(
    (t) => t.plannedStart && new Date(t.plannedStart).toISOString().slice(0, 10) === dayIso,
  )
}

function DraftTaskBlock({
  draft,
  workColor = DEFAULT_DRAFT_WORK_COLOR,
  travelColor = DEFAULT_DRAFT_TRAVEL_COLOR,
  overrideTopPx,
  interactive,
  onPointerDown,
}: {
  draft: DraftBlock
  workColor?: string
  travelColor?: string
  overrideTopPx?: number
  interactive?: boolean
  onPointerDown?: (e: React.PointerEvent) => void
}) {
  const workStart = new Date(draft.plannedStart)
  const travelStart = new Date(workStart.getTime() - draft.travelDuration * 60_000)

  const totalMinutes = draft.travelDuration + draft.workDuration + draft.departureDuration
  const calculatedTop = getTopPx(travelStart.toISOString())
  const top = overrideTopPx ?? calculatedTop
  const height = Math.max(getHeightPx(totalMinutes), 24)

  const travelPx = getHeightPx(draft.travelDuration)
  const workPx = getHeightPx(draft.workDuration)
  const departurePx = getHeightPx(draft.departureDuration)

  const timeLabel = `${String(workStart.getHours()).padStart(2, '0')}:${String(workStart.getMinutes()).padStart(2, '0')}`

  return (
    <div
      className={`absolute left-0.5 right-0.5 flex flex-col rounded overflow-hidden z-20 gap-px ${
        interactive ? 'cursor-grab active:cursor-grabbing' : 'pointer-events-none'
      }`}
      style={{
        top,
        height,
        outline: '2px dashed #124093',
        outlineOffset: '-2px',
        borderRadius: 6,
        touchAction: interactive ? 'none' : undefined,
      }}
      onPointerDown={onPointerDown}
      onClick={interactive ? (e) => e.stopPropagation() : undefined}
    >
      {draft.travelDuration > 0 && (
        <div
          className="flex items-center px-1 text-white text-[11px] font-semibold overflow-hidden shrink-0 rounded-sm opacity-90"
          style={{ backgroundColor: travelColor, height: travelPx, minHeight: 12 }}
        >
          Anfahrt
        </div>
      )}
      <div
        className="flex flex-col justify-start px-1 text-white text-[11px] font-semibold overflow-hidden rounded-sm py-0.5 opacity-90"
        style={{ backgroundColor: workColor, height: workPx, minHeight: 16, flexShrink: 0 }}
      >
        <span className="font-semibold leading-tight">{timeLabel}</span>
        <span className="leading-tight line-clamp-2 break-words">{draft.title}</span>
      </div>
      {draft.departureDuration > 0 && (
        <div
          className="flex items-center px-1 text-white text-[11px] font-semibold overflow-hidden shrink-0 rounded-sm opacity-90"
          style={{ backgroundColor: travelColor, height: departurePx, minHeight: 12 }}
        >
          Abfahrt
        </div>
      )}
    </div>
  )
}

export function PlanningDayColumn({
  dayIso,
  isToday,
  technicianData,
  existingTasks,
  currentUserId,
  draft,
  onTimeSelect,
  draftColors,
  drag,
  onDragStart,
}: Props) {
  const gridRef = useRef<HTMLDivElement>(null)
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const pointerMovedRef = useRef(false)
  const date = new Date(dayIso + 'T12:00:00')
  const dow = date.getDay()
  const dayName = DAY_NAMES[dow]
  const dayNum = date.getDate()
  const monthName = MONTH_FORMATTER.format(date)

  // Per-day data extraction (same as DayColumn)
  const working = technicianData ? isWorkingDay(technicianData.workingDays, dayIso) : true
  const appointments = technicianData
    ? filterForDay(technicianData.appointments.filter((a) => !a.needsRescheduling), dayIso)
    : []
  const tmEntries = technicianData ? filterTMForDay(technicianData.timeManagementEntries, dayIso) : []
  const breakBlock = technicianData ? resolveBreakForDay(technicianData, dayIso) : null
  const { transferTravel, transferDeparture } = useMemo(() => computeTransfer(appointments), [appointments])
  const dayTasks = filterTasksForDay(existingTasks, dayIso)

  const overlapLayout = useMemo(() => {
    const rects = [
      ...appointments.map(getAppointmentRect),
      ...dayTasks.flatMap((t) => getTaskRect(t) ?? []),
      ...tmEntries.map((e) => getTMRect(e, dayIso)),
      ...(working && breakBlock ? [getBreakRect('break', breakBlock.startTimeIso)] : []),
    ]
    return computeOverlapLayout(rects)
  }, [appointments, dayTasks, tmEntries, dayIso, working, breakBlock])

  // Shift restriction overlays
  const shiftStartHour = technicianData?.shiftStart ? getHourFromIso(technicianData.shiftStart) : null
  const shiftEndHour = technicianData?.shiftEnd ? getHourFromIso(technicianData.shiftEnd) : null

  const preShiftHeight =
    shiftStartHour != null && shiftStartHour > CAL_START_HOUR
      ? (shiftStartHour - CAL_START_HOUR) * HOUR_HEIGHT
      : 0

  const postShiftTop =
    shiftEndHour != null && shiftEndHour < CAL_END_HOUR
      ? (shiftEndHour - CAL_START_HOUR) * HOUR_HEIGHT
      : CAL_TOTAL_HEIGHT

  // Drag state for this column
  const isDraftDragging = drag?.type === 'draft' && drag.dayIso === dayIso
  const isBreakDragging = drag?.type === 'break' && drag.dayIso === dayIso
  const isDraftDraggingElsewhere = drag?.type === 'draft' && drag.dayIso !== dayIso

  // While dragging the draft, show it in the column the pointer is currently over (drag.dayIso).
  // Hide it from the original column so it doesn't appear in two places at once.
  const draftOriginDay = draft ? new Date(draft.plannedStart).toISOString().slice(0, 10) : null
  const draftForThisDay =
    draft &&
    (isDraftDragging || (!isDraftDraggingElsewhere && draftOriginDay === dayIso))
      ? draft
      : null

  // Block top positions (for drag offset calculation)
  const draftTopPx = draftForThisDay
    ? getTopPx(
        new Date(
          new Date(draftForThisDay.plannedStart).getTime() - draftForThisDay.travelDuration * 60_000,
        ).toISOString(),
      )
    : 0

  const breakTopPx = breakBlock ? getTopPx(breakBlock.startTimeIso) : 0

  // During break drag, use raw topPx from drag state for smooth visual movement
  const breakOverrideTopPx = isBreakDragging && drag ? drag.topPx : undefined

  function handleGridPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    pointerStartRef.current = { x: e.clientX, y: e.clientY }
    pointerMovedRef.current = false
  }

  function handleGridPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const start = pointerStartRef.current
    if (!start || pointerMovedRef.current) return
    if (Math.abs(e.clientX - start.x) > 10 || Math.abs(e.clientY - start.y) > 10) {
      pointerMovedRef.current = true
    }
  }

  function handleGridClick(e: React.MouseEvent<HTMLDivElement>) {
    // Don't place draft while dragging or swiping
    if (drag) return
    if (pointerMovedRef.current) return
    if (!working) return

    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top

    if (preShiftHeight > 0 && y < preShiftHeight) return
    if (postShiftTop < CAL_TOTAL_HEIGHT && y > postShiftTop) return

    const minutesFromStart = (y / HOUR_HEIGHT) * 60
    const totalMinutes = CAL_START_HOUR * 60 + minutesFromStart
    const rounded = Math.round(totalMinutes / 15) * 15
    const hours = Math.floor(rounded / 60)
    const mins = rounded % 60
    onTimeSelect(dayIso, Math.min(hours, 23), Math.min(mins, 59))
  }

  function handleDraftPointerDown(e: React.PointerEvent) {
    if (!onDragStart || !gridRef.current) return
    onDragStart('draft', dayIso, e, gridRef.current, draftTopPx)
  }

  function handleBreakPointerDown(e: React.PointerEvent) {
    if (!onDragStart || !gridRef.current) return
    e.stopPropagation()
    onDragStart('break', dayIso, e, gridRef.current, breakTopPx)
  }

  return (
    <div className="flex flex-col flex-1 min-w-0 border-l border-gray-200">
      {/* Day header */}
      <div
        className={`sticky top-0 z-50 flex flex-col items-center py-1.5 border-b border-gray-200 ${
          isToday ? 'bg-blue-50' : 'bg-white'
        }`}
      >
        <span className={`text-xs font-medium ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
          {dayName}
        </span>
        <span
          className={`text-base font-bold leading-tight ${
            isToday ? 'text-blue-700' : 'text-gray-800'
          }`}
        >
          {dayNum}
        </span>
        <span className="text-[10px] text-gray-400">{monthName}</span>
      </div>

      {/* Tappable time grid */}
      <div
        ref={gridRef}
        className="relative cursor-pointer"
        style={{ height: CAL_TOTAL_HEIGHT, touchAction: 'pan-y' }}
        onPointerDown={handleGridPointerDown}
        onPointerMove={handleGridPointerMove}
        onClick={handleGridClick}
        data-planning-grid="true"
        data-day-iso={dayIso}
      >
        {/* Hour grid lines */}
        {CAL_HOURS.map((h, i) => (
          <div
            key={h}
            className="absolute left-0 right-0 border-t border-gray-100"
            style={{ top: i * HOUR_HEIGHT }}
          />
        ))}

        {/* Non-working day / shift restriction overlays */}
        {!working ? (
          <div className="absolute inset-0 bg-gray-300/50 z-[1]" />
        ) : (
          <>
            {preShiftHeight > 0 && (
              <div
                className="absolute left-0 right-0 top-0 bg-gray-300/50 z-[1]"
                style={{ height: preShiftHeight }}
              />
            )}
            {postShiftTop < CAL_TOTAL_HEIGHT && (
              <div
                className="absolute left-0 right-0 bottom-0 bg-gray-300/50 z-[1]"
                style={{ top: postShiftTop }}
              />
            )}
          </>
        )}

        {/* Appointment blocks (dimmed, non-interactive) */}
        {appointments.map((appt) => {
          const layout = overlapLayout.get(appt.id)
          return (
            <div
              key={appt.id}
              className="absolute top-0 bottom-0 right-0 opacity-60 pointer-events-none"
              style={{ left: layout?.left ?? '2px', zIndex: layout?.zIndex ?? 10 }}
            >
              <WorkerAppointmentBlock
                appointment={appt}
                isTransferTravel={transferTravel.has(appt.id)}
                isTransferDeparture={transferDeparture.has(appt.id)}
                onClick={() => {}}
              />
            </div>
          )
        })}

        {/* Existing tasks (dimmed, non-interactive) */}
        {dayTasks.map((task) => {
          const layout = overlapLayout.get(String(task.id))
          return (
            <div
              key={task.id}
              className="absolute top-0 bottom-0 right-0 opacity-40 pointer-events-none"
              style={{ left: layout?.left ?? '2px', zIndex: layout?.zIndex ?? 10 }}
            >
              <WorkerTaskBlock task={task} onClick={() => {}} />
            </div>
          )
        })}

        {/* TM entry blocks (dimmed, non-interactive) */}
        {tmEntries.map((entry) => {
          const layout = overlapLayout.get(String(entry.id))
          return (
            <div
              key={entry.id}
              className="absolute top-0 bottom-0 right-0 opacity-60 pointer-events-none"
              style={{ left: layout?.left ?? '2px', zIndex: layout?.zIndex ?? 10 }}
            >
              <WorkerTMBlock entry={entry} dayIso={dayIso} />
            </div>
          )
        })}

        {/* Break block — draggable */}
        {working && breakBlock && (() => {
          const layout = overlapLayout.get('break')
          return (
            <WorkerBreakBlock
              startTimeIso={breakBlock.startTimeIso}
              overrideTopPx={breakOverrideTopPx}
              left={layout?.left ?? '2px'}
              zIndex={layout?.zIndex ?? 10}
              interactive={!!onDragStart}
              isDragging={isBreakDragging}
              onPointerDown={onDragStart ? handleBreakPointerDown : undefined}
              onClick={onDragStart ? (e) => e.stopPropagation() : undefined}
            />
          )
        })()}

        {/* Draft block — draggable */}
        {draftForThisDay && (
          <DraftTaskBlock
            draft={draftForThisDay}
            workColor={draftColors?.work}
            travelColor={draftColors?.travel}
            interactive={!!onDragStart}
            overrideTopPx={isDraftDragging ? drag.topPx : undefined}
            onPointerDown={onDragStart ? handleDraftPointerDown : undefined}
          />
        )}
      </div>
    </div>
  )
}
